import { ReactNode } from 'react'

import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'

type Props = {
    status: number
    icon: ReactNode
    title: string
    description: ReactNode
    actions: ReactNode
    reference?: string
}

export default function ErrorPageLayout(props: Readonly<Props>) {
    const { status, icon, title, description, actions, reference } = props

    return (
        <Box
            sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'flex-start',
                px: 2,
                py: { xs: 4, md: 8 },
            }}
        >
            <Paper
                elevation={0}
                sx={{
                    width: '100%',
                    maxWidth: 560,
                    px: { xs: 3, sm: 5 },
                    py: 5,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    textAlign: 'center',
                }}
            >
                <Stack spacing={2} sx={{ alignItems: 'center' }}>
                    <Box
                        sx={{
                            width: 64,
                            height: 64,
                            borderRadius: '50%',
                            bgcolor: 'action.hover',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'text.secondary',
                        }}
                    >
                        {icon}
                    </Box>

                    <Typography
                        variant="overline"
                        sx={{ color: 'text.secondary', letterSpacing: '0.12em' }}
                    >
                        Error {status}
                    </Typography>

                    <Typography variant="h5" component="h1" sx={{ fontWeight: 700 }}>
                        {title}
                    </Typography>

                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        {description}
                    </Typography>

                    <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        spacing={1.5}
                        sx={{ pt: 1, justifyContent: 'center', width: '100%' }}
                    >
                        {actions}
                    </Stack>

                    {reference ? (
                        <Typography
                            variant="caption"
                            sx={{ color: 'text.disabled', fontFamily: 'monospace', pt: 1 }}
                        >
                            Error reference: {reference}
                        </Typography>
                    ) : null}
                </Stack>
            </Paper>
        </Box>
    )
}
